import React, { useMemo, useState } from 'react';
import { RotateCcwIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { daysUntilPurge, emptyTrash, purgeLead, restoreLead } from '../../services/leadService';
import { restoreForm } from '../../services/formService';
import { restoreLanding } from '../../services/landingService';
import { formatDate, fullName } from '../../utils/format';
import { Lead } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';

/**
 * Papelera de la organización. Los elementos usan borrado lógico y se
 * conservan 30 días antes de eliminarse definitivamente.
 */
export function Trash() {
  const {
    user,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    trashedLeads,
    trashedForms,
    trashedLandings
  } = useWorkspace();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmPurge, setConfirmPurge] = useState<Lead | 'seleccion' | 'todo' | null>(null);
  const [working, setWorking] = useState(false);
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const canDelete = hasPermission('leads.delete');
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return trashedLeads;
    return trashedLeads.filter(lead => [lead.firstName, lead.lastName, lead.company, lead.email].join(' ').toLowerCase().includes(term));
  }, [trashedLeads, search]);
  async function confirmAction() {
    setWorking(true);
    if (confirmPurge === 'todo') await emptyTrash(organizationId);else if (confirmPurge === 'seleccion') {
      for (const id of selected) await purgeLead(id);
      setSelected([]);
    } else if (confirmPurge) await purgeLead(confirmPurge.id);
    setWorking(false);
    setConfirmPurge(null);
  }
  const empty = trashedLeads.length === 0 && trashedForms.length === 0 && trashedLandings.length === 0;
  return <>
      <PageHeader title="Papelera" description="Los elementos eliminados se conservan 30 días antes de borrarse definitivamente." actions={canDelete && trashedLeads.length > 0 ? <Button variant="destructive" onClick={() => setConfirmPurge('todo')} data-id="element-1674" data-mp="1rgjnk6xug9qt">
              Vaciar papelera
            </Button> : undefined} data-id="element-1673" data-mp="208bwueb4tctt" />

      {empty ? <EmptyState icon={<Trash2Icon className="h-5 w-5" data-id="element-1676" data-mp="1hinfjo07duwx" />} title="La papelera está vacía" description="Aquí aparecerán los leads, formularios y landings que elimines, por si necesitas recuperarlos." data-id="element-1675" data-mp="1mfm84247xtt0" /> : <Tabs defaultValue="leads" data-id="element-1677" data-mp="1cln6d4824c1l">
          <TabsList data-id="element-1678" data-mp="18hfh95xosu2u">
            <TabsTrigger value="leads" data-id="element-1679" data-mp="18j0dczzngl2t">Leads ({trashedLeads.length})</TabsTrigger>
            <TabsTrigger value="formularios" data-id="element-1680" data-mp="3k5u5mn35n6oe">
              Formularios ({trashedForms.length})
            </TabsTrigger>
            <TabsTrigger value="landings" data-id="element-1681" data-mp="3o5l1343mmaz9">
              Landings ({trashedLandings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads" data-id="element-1682" data-mp="3t462v7yj12pt">
            <div className="relative mb-4 max-w-md" data-id="element-1683" data-mp="1zxuk3m2096w">
              <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-1684" data-mp="1xlddso4e2uw" />
              <input type="search" aria-label="Buscar en la papelera" placeholder="Buscar por nombre, empresa o email" value={search} onChange={e => setSearch(e.target.value)} className="h-11 w-full rounded-xl border border-border bg-background pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1685" data-mp="35hg2eb1ttg7b" />
            </div>

            {selected.length > 0 && canDelete && <div className="mb-4 flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1686" data-mp="2mnu3j3jyxesn">
                <p className="text-sm font-medium text-foreground" data-id="element-1687" data-mp="o94lvv1wc1cp">
                  {selected.length} seleccionados
                </p>
                <div className="flex flex-col gap-2 sm:flex-row" data-id="element-1688" data-mp="121kurchhatlj">
                  <Button variant="secondary" onClick={async () => {
              for (const id of selected) await restoreLead(id, authorName);
              setSelected([]);
            }} data-id="element-1689" data-mp="x7zu1coi3lin">
                    Restaurar
                  </Button>
                  <Button variant="destructive" onClick={() => setConfirmPurge('seleccion')} data-id="element-1690" data-mp="1kydlp236bicp">
                    Eliminar definitivamente
                  </Button>
                </div>
              </div>}

            <ul className="grid gap-3" data-id="element-1691" data-mp="3mpgzg08d1d1h">
              {filtered.map(lead => <li key={lead.id} className="flex flex-col gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm sm:flex-row sm:items-center" data-id="element-1692" data-mp="3mpz33hxcqtjf">
                  {canDelete && <Checkbox id={`trash-${lead.id}`} label="" aria-label={`Seleccionar ${lead.firstName}`} checked={selected.includes(lead.id)} onChange={e => setSelected(prev => e.target.checked ? [...prev, lead.id] : prev.filter(id => id !== lead.id))} data-id="element-1693" data-mp="1x9msag0d7vun" />}
                  <div className="min-w-0 flex-1" data-id="element-1694" data-mp="1shv8qpfmmqe0">
                    <p className="truncate font-medium text-foreground" data-id="element-1695" data-mp="1sgk46necxelu">
                      {fullName(lead.firstName, lead.lastName)}
                    </p>
                    <p className="truncate text-sm text-muted-foreground" data-id="element-1696" data-mp="1jhq2iim65y1f">
                      {lead.company} · {lead.email}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground" data-id="element-1697" data-mp="15j8juzb4fbqd">
                      Eliminado por {lead.deletedBy ?? 'Sistema'} el{' '}
                      {formatDate(lead.deletedAt ?? '')} · Se eliminará
                      definitivamente en {daysUntilPurge(lead.deletedAt)} días
                    </p>
                  </div>
                  <div className="flex flex-none gap-2" data-id="element-1698" data-mp="gockcvkviuqi">
                    <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<RotateCcwIcon className="h-4 w-4" data-id="element-1700" data-mp="boggcwc6f3ay" />} onClick={() => restoreLead(lead.id, authorName)} data-id="element-1699" data-mp="gpw10nafz7st">
                      Restaurar
                    </Button>
                    {canDelete && <Button variant="destructive" size="sm" className="min-h-[44px]" onClick={() => setConfirmPurge(lead)} data-id="element-1701" data-mp="2pl7fchqzf3y">
                        Eliminar
                      </Button>}
                  </div>
                </li>)}
              {filtered.length === 0 && <li className="rounded-2xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-id="element-1702" data-mp="vfuuwduyrx2h">
                  No hay leads en la papelera.
                </li>}
            </ul>
          </TabsContent>

          <TabsContent value="formularios" data-id="element-1703" data-mp="1j3l145q028ir">
            <ul className="grid gap-3" data-id="element-1704" data-mp="1j4e3yvlu8dvm">
              {trashedForms.map(form => <li key={form.id} className="flex items-center gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm" data-id="element-1705" data-mp="1ebbht8je46pg">
                  <div className="min-w-0 flex-1" data-id="element-1706" data-mp="1lpc1k8khnrzt">
                    <p className="truncate font-medium text-foreground" data-id="element-1707" data-mp="1qm2jz2jrmzoe">{form.name}</p>
                    <p className="text-xs text-muted-foreground" data-id="element-1708" data-mp="1vlx6yv0f7546">
                      Se eliminará definitivamente en {daysUntilPurge(form.deletedAt)}{' '}
                      días
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<RotateCcwIcon className="h-4 w-4" data-id="element-1710" data-mp="29kwvm2nxnk0c" />} onClick={() => restoreForm(form.id)} data-id="element-1709" data-mp="24lkb3okjap0x">
                    Restaurar
                  </Button>
                </li>)}
              {trashedForms.length === 0 && <li className="rounded-2xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-id="element-1711" data-mp="2jbrj1acx0pnd">
                  No hay formularios eliminados.
                </li>}
            </ul>
          </TabsContent>

          <TabsContent value="landings" data-id="element-1712" data-mp="2schay2z21vvt">
            <ul className="grid gap-3" data-id="element-1713" data-mp="3dmwbuifzfxxi">
              {trashedLandings.map(landing => <li key={landing.id} className="flex items-center gap-3 rounded-2xl border border-border bg-background p-4 shadow-sm" data-id="element-1714" data-mp="3dozcrxiobbh0">
                  <div className="min-w-0 flex-1" data-id="element-1715" data-mp="3nkspbgwe74ys">
                    <p className="truncate font-medium text-foreground" data-id="element-1716" data-mp="3nkatumhk0urx">
                      {landing.name}
                    </p>
                    <p className="text-xs text-muted-foreground" data-id="element-1717" data-mp="2uwepcvlqtykl">
                      Se eliminará definitivamente en{' '}
                      {daysUntilPurge(landing.deletedAt)} días
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<RotateCcwIcon className="h-4 w-4" data-id="element-1719" data-mp="2zvrj80b185wo" />} onClick={() => restoreLanding(landing.id)} data-id="element-1718" data-mp="34n9lqsspeejr">
                    Restaurar
                  </Button>
                </li>)}
              {trashedLandings.length === 0 && <li className="rounded-2xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-id="element-1720" data-mp="277c99s6rcds7">
                  No hay landings eliminadas.
                </li>}
            </ul>
          </TabsContent>
        </Tabs>}

      <ConfirmDialog open={Boolean(confirmPurge)} title="Eliminar definitivamente" description="Esta acción eliminará permanentemente el lead y toda su información. No se podrá recuperar." confirmLabel="Eliminar definitivamente" loading={working} onCancel={() => setConfirmPurge(null)} onConfirm={confirmAction} data-id="element-1721" data-mp="rgt83lqcrl51" />
    </>;
}